/**
 * Passkey Registration Form Component
 *
 * Allows users to register new passkeys for secure authentication.
 * Provides a user-friendly interface for WebAuthn credential creation.
 */

'use client';

import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Steps, message } from 'antd';
import { KeyOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import webauthnService from '@/domains/auth/services/webauthn-service';

const { Title, Text } = Typography;
interface PasskeyRegistrationFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

interface RegistrationFormData {
  username: string;
  displayName: string;
}

export const PasskeyRegistrationForm: React.FC<PasskeyRegistrationFormProps> = ({
  onSuccess,
  onError,
  className
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [registrationOptions, setRegistrationOptions] = useState<any>(null);

  const handleStartRegistration = async (values: RegistrationFormData) => {
    setLoading(true);
    setError(null);

    try {
      // Check if WebAuthn is supported
      if (!window.navigator.credentials || !window.PublicKeyCredential) {
        throw new Error('Passkeys are not supported in this browser');
      }

      // Check if passkeys are supported on this platform
      const isPasskeySupported = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!isPasskeySupported) {
        throw new Error('Passkeys are not supported on this device');
      }

      console.log('🔐 PasskeyRegistrationForm: Starting registration for:', values.username);

      // Start passkey registration
      const options = await webauthnService.startRegistration(values.username, values.displayName);
      console.log('🔐 PasskeyRegistrationForm: Received registration options:', options);

      setRegistrationOptions(options);
      setCurrentStep(1);
      message.success('Registration options received. Please complete the passkey setup.');
    } catch (err) {
      console.error('Passkey registration error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start passkey registration';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async () => {
    if (!registrationOptions) {
      setError('No registration options available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔐 PasskeyRegistrationForm: Completing registration');

      // Complete passkey registration
      const success = await webauthnService.completeRegistration(registrationOptions);
      console.log('🔐 PasskeyRegistrationForm: Registration result:', success);

      if (success) {
        setCurrentStep(2);
        message.success('Passkey registered successfully!');
        onSuccess?.();
      } else {
        throw new Error('Passkey registration failed');
      }
    } catch (err) {
      console.error('Passkey registration completion error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete passkey registration';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setRegistrationOptions(null);
    setError(null);
    form.resetFields();
  };

  return (
    <Card className={className} style={{ maxWidth: '500px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <Title level={2}>
          <KeyOutlined style={{ marginRight: '8px' }} />
          Passkey Registration
        </Title>
        <Text type="secondary">Set up secure passwordless authentication</Text>
      </div>

      {/* Error Display */}
      {error && (
        <Alert
          title="Registration Error"
          description={error}
          type="error"
          showIcon
          closable={{ onClose: () => setError(null) }}
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Progress Steps */}
      <Steps
        current={currentStep}
        style={{ marginBottom: '24px' }}
        items={[
          { title: 'Enter Details', content: 'Provide username and display name' },
          { title: 'Create Passkey', content: 'Set up your passkey' },
          { title: 'Complete', content: 'Registration finished' },
        ]}
      />

      {/* Step 1: Enter Details */}
      {currentStep === 0 && (
        <Form
          form={form}
          name="passkey-registration"
          onFinish={handleStartRegistration}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please enter your username' },
              { min: 3, message: 'Username must be at least 3 characters' }
            ]}
          >
            <Input
              placeholder="Enter your username"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="displayName"
            label="Display Name"
            rules={[
              { required: true, message: 'Please enter your display name' },
              { min: 2, message: 'Display name must be at least 2 characters' }
            ]}
          >
            <Input
              placeholder="Enter your display name"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
            >
              Start Passkey Registration
            </Button>
          </Form.Item>
        </Form>
      )}

      {/* Step 2: Create Passkey */}
      {currentStep === 1 && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '24px' }}>
            <Text>
              Your browser will now prompt you to create a passkey. This may involve:
            </Text>
            <ul style={{ textAlign: 'left', marginTop: '16px' }}>
              <li>Using your fingerprint or face ID</li>
              <li>Using a PIN or pattern</li>
              <li>Using a security key</li>
            </ul>
          </div>

          <Button
            type="primary"
            onClick={handleCompleteRegistration}
            loading={loading}
            size="large"
            icon={loading ? <LoadingOutlined /> : <KeyOutlined />}
          >
            {loading ? 'Creating Passkey...' : 'Create Passkey'}
          </Button>

          <Button
            style={{ marginLeft: '8px' }}
            onClick={handleReset}
            disabled={loading}
          >
            Start Over
          </Button>
        </div>
      )}

      {/* Step 3: Complete */}
      {currentStep === 2 && (
        <div style={{ textAlign: 'center' }}>
          <CheckCircleOutlined
            style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }}
          />
          <Title level={3} style={{ color: '#52c41a' }}>
            Passkey Registered Successfully!
          </Title>
          <Text>
            Your passkey has been registered and is ready to use for secure authentication.
          </Text>
          <div style={{ marginTop: '24px' }}>
            <Button type="primary" onClick={onSuccess}>
              Continue
            </Button>
            <Button style={{ marginLeft: '8px' }} onClick={handleReset}>
              Register Another Passkey
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
